/* global console */

import { fetchWithRetry, readApiError } from "../utils";
import { buildApiUrl } from "../config";
import { withCredentials } from "./apiClient";

export interface ExtractedImage {
  page_number: number;
  image_index: number;
  width: number;
  height: number;
  format: string;
  base64_data: string;
}

export interface ExtractionResult {
  success: boolean;
  total_pages: number;
  total_images: number;
  images: ExtractedImage[];
  error?: string;
}

export async function extractImagesFromPdf(
  file: File,
  signal?: AbortSignal
): Promise<ExtractionResult> {
  const formData = new FormData();
  formData.append("file", file);

  console.log(`[PDF] Uploading ${file.name} (${(file.size / 1024).toFixed(1)}KB)`);

  const response = await fetchWithRetry(
    buildApiUrl("/api/pdf/extract-images"),
    withCredentials({ method: "POST", body: formData, signal }),
    1,
    "PDF"
  );

  if (!response.ok) {
    const error = await readApiError(response);
    throw new Error(`PDF extraction failed: ${error}`);
  }

  const result: ExtractionResult = await response.json();
  console.log(`[PDF] Extracted ${result.total_images} images from ${result.total_pages} pages`);

  return result;
}

export async function extractVectorGraphics(
  file: File,
  signal?: AbortSignal
): Promise<ExtractionResult> {
  const formData = new FormData();
  formData.append("file", file);

  console.log(`[PDF] Extracting vectors from ${file.name}`);

  const response = await fetchWithRetry(
    buildApiUrl("/api/pdf/extract-vectors"),
    withCredentials({ method: "POST", body: formData, signal }),
    1,
    "PDF"
  );

  if (!response.ok) {
    const error = await readApiError(response);
    throw new Error(`Vector extraction failed: ${error}`);
  }

  const result: ExtractionResult = await response.json();
  console.log(`[PDF] Extracted ${result.total_images} vector graphics`);

  return result;
}

export async function extractPagesAsImages(
  file: File,
  signal?: AbortSignal
): Promise<ExtractionResult> {
  const formData = new FormData();
  formData.append("file", file);

  console.log(`[PDF] Rendering pages from ${file.name}`);

  const response = await fetchWithRetry(
    buildApiUrl("/api/pdf/extract-pages"),
    withCredentials({ method: "POST", body: formData, signal }),
    1,
    "PDF"
  );

  if (!response.ok) {
    const error = await readApiError(response);
    throw new Error(`PDF page rendering failed: ${error}`);
  }

  const result: ExtractionResult = await response.json();
  console.log(`[PDF] Rendered ${result.total_images} pages as images`);

  return result;
}
